import { Button } from '@freelancer-os/ui/src/components/ui/button';
import { ClipboardCopy, RefreshCw, Zap } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Markdown from 'react-markdown';
import type { ChatMessage } from '../../../stores/chatStore';
import { SuggestionGrid } from './SuggestionGrid';

export type { ChatMessage };

interface ChatMessagesProps {
  messages: ChatMessage[];
  status: string | null;
  onSuggestionPick?: (text: string, attachLabel?: string) => void;
}

export function ChatMessages({ messages, status, onSuggestionPick }: ChatMessagesProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // The store seeds a default system welcome message, so `messages.length === 0`
  // is never true on a fresh conversation. Treat "no user/assistant turn yet" as
  // empty — system rows are decorative.
  const hasRealTurn = messages.some((m) => m.role === 'user' || m.role === 'assistant');
  const isEmpty = !hasRealTurn && !status;

  return (
    <div
      ref={scrollRef}
      className="flex-1 overflow-y-auto px-5 py-6 [&::-webkit-scrollbar]:w-[3px] [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-surface-200 hover:[&::-webkit-scrollbar-thumb]:bg-surface-300"
    >
      {isEmpty && onSuggestionPick ? (
        <SuggestionGrid onPick={onSuggestionPick} />
      ) : (
        <div className="max-w-[720px] mx-auto space-y-6">
          {messages.map((msg) => {
            if (msg.role === 'system') return <SystemMessage key={msg.id} msg={msg} />;
            if (msg.role === 'user') return <UserMessage key={msg.id} msg={msg} />;
            return <AssistantMessage key={msg.id} msg={msg} />;
          })}
          {status && <StatusIndicator status={status} />}
        </div>
      )}
    </div>
  );
}

function SystemMessage({ msg }: { msg: ChatMessage }) {
  return (
    <div className="text-center animate-[fade-up_0.25s_ease-out]">
      <div className="inline-flex items-center gap-2 px-4 py-2 bg-surface-100 border border-surface-200">
        <Zap className="w-3.5 h-3.5 text-brand-500" strokeWidth={2.5} />
        <span className="text-[12px] font-medium text-surface-500">
          {msg.content}
        </span>
      </div>
    </div>
  );
}

function UserMessage({ msg }: { msg: ChatMessage }) {
  return (
    <div className="flex justify-end animate-[fade-up_0.25s_ease-out]">
      <div className="max-w-[85%]">
        <div className="bg-brand-500 text-white px-4 py-3">
          <p className="text-[14px] leading-relaxed whitespace-pre-wrap">{msg.content}</p>
        </div>
        <div className="text-[11px] text-surface-400 mt-1.5 text-right pr-1">
          {msg.timestamp}
        </div>
      </div>
    </div>
  );
}

function AssistantMessage({ msg }: { msg: ChatMessage }) {
  const { t } = useTranslation('chat');
  return (
    <div className="flex gap-3 animate-[fade-up_0.25s_ease-out]">
      <div className="w-7 h-7 bg-gradient-to-br from-brand-400 to-emerald-500 flex items-center justify-center shrink-0 mt-0.5">
        <Zap className="w-3.5 h-3.5 text-white" strokeWidth={2.5} />
      </div>
      <div className="max-w-[85%]">
        <div className="bg-surface-0 border border-surface-200 px-4 py-3 shadow-[0_1px_2px_rgba(0,0,0,0.04)]">
          <div className="text-[14px] leading-relaxed text-surface-700 prose prose-sm prose-neutral max-w-none [&>p]:mb-2 [&>p:last-child]:mb-0 [&>ul]:list-disc [&>ul]:pl-4 [&>ol]:list-decimal [&>ol]:pl-4">
            {msg.streaming ? (
              <span className="after:content-['▋'] after:animate-[blink-cursor_0.8s_step-end_infinite] after:text-brand-500 after:ml-px">
                <Markdown>{msg.content}</Markdown>
              </span>
            ) : (
              <Markdown>{msg.content}</Markdown>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 mt-2 pl-1">
          <span className="text-[11px] text-surface-400">{msg.timestamp}</span>
          {!msg.streaming && (
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-auto px-2.5 py-1 bg-surface-100 border-surface-200 text-[11.5px] font-medium text-surface-500 hover:bg-surface-200 shadow-none"
              >
                <ClipboardCopy className="w-3 h-3" />
                {t('actions.copyJSON')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-auto px-2.5 py-1 bg-surface-100 border-surface-200 text-[11.5px] font-medium text-surface-500 hover:bg-surface-200 shadow-none"
              >
                <RefreshCw className="w-3 h-3" />
                {t('actions.regenerate')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatusIndicator({ status }: { status: string }) {
  return (
    <div className="flex items-center gap-2 animate-[fade-up_0.25s_ease-out]">
      <div className="flex items-center gap-2 px-3 py-2 bg-brand-50 border border-brand-200 rounded-none">
        <Zap className="w-3.5 h-3.5 text-brand-500 animate-pulse" strokeWidth={2.5} />
        <span className="text-[12.5px] font-medium text-brand-700">{status}</span>
      </div>
    </div>
  );
}
